import { useState, useRef } from "react";
import { Typography, Button, Paper, IconButton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TemplateInstruction from "../../assets/template/Bulk_Upload_Template_Instructions.xlsx";
import CloseIcon from "@mui/icons-material/Close";
import "./styles.css";

const Dropzone = ({
  selectedFile,
  setSelectedFile,
  error,
  setError,
  templateUrl,
  allowedFormats,
  typeUpload,
  searchType,
  searchIn,
  handleDownloadClick,
  handleTemplateClick,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  const validateAndSetFile = (file, maxSize) => {
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (file && !file.name.endsWith(allowedFormats)) {
      setError(`Invalid file type. Please upload a ${allowedFormats} file.`);
      setSelectedFile(null);
    } else if (file.size > maxSizeBytes) {
      setError(`File size exceeds the ${maxSize} MB limit.`);
      setSelectedFile(null);
    } else {
      setSelectedFile(file);
      setError("");
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith(allowedFormats)) {
      setError("");
      setSelectedFile(file);
      validateAndSetFile(file, 50);
    } else {
      setError(`Invalid file type. Please upload a ${allowedFormats} file.`);
      setSelectedFile(null);
    }
    event.target.value = null;
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    console.log(file);
    if (file && file.name.endsWith(allowedFormats)) {
      setError("");
      setSelectedFile(file);
      validateAndSetFile(file, 50);
    } else {
      setError(`Invalid file type. Please upload a ${allowedFormats} file.`);
      setSelectedFile(null);
    }
    event.target.value = null;
  };
  const handleUploadClick = () => {
    inputRef.current.click();
  };

  const handleRemoveFile = (event) => {
    event.stopPropagation();
    setSelectedFile(null);
    setError("");
  };
  const handleDownloadClicks = () => {
    handleDownloadClick();
  };

  const handleDownloadClicksInstructions = () => {
    const link = document.createElement("a");
    link.href = TemplateInstruction;
    link.download = "Bulk_Upload_Template_Instructions.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {templateUrl && (
        <Typography
          variant="body2"
          className="_wg-dynamic-text"
          color="text.secondary"
          style={{ color: "#000" }}
        >
          Don't have a file ready?{" "}
          <a
            href={templateUrl}
            download
            onClick={(event) => {
              if (handleTemplateClick) {
                event.preventDefault();
                handleTemplateClick();
              }
            }}
            style={{ color: "#d32f2f", fontWeight: 500 }}
          >
            Download the template file
          </a>{" "}
          {typeUpload !== "translitration" ? (
            <>
              with all required column headers.
              {typeUpload === "bulk-upload" && (
                <>
                  {" "}
                  You can download a sample file with instructions{" "}
                  <Typography
                    component="span"
                    onClick={handleDownloadClicksInstructions}
                    sx={{
                      color: "#D31710",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: "medium",
                      "&:hover": {
                        textDecoration: "none",
                      },
                    }}
                    style={{ fontSize: "15px" }}
                  >
                    here
                  </Typography>
                  .
                </>
              )}{" "}
              Download Reference LOV file from{" "}
              <Typography
                component="span"
                onClick={handleDownloadClicks}
                sx={{
                  color: "#D31710",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: "medium",
                  "&:hover": {
                    textDecoration: "none",
                  },
                }}
                style={{ fontSize: "15px" }}
              >
                here
              </Typography>
              .
            </>
          ) : (
            <>with all required column headers and instructions.</>
          )}
        </Typography>
      )}
      <div
        className={`dropzone ${isDragging ? "dragging" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleUploadClick}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: "#d32f2f", mb: 1 }} />
        <Typography variant="body1" className="_wg-dynamic-text mb-2">
          Drag and drop your {allowedFormats} file here, or click to browse
        </Typography>
        <Button
          component="label"
          variant="outlined"
          color="error"
          tabIndex={-1}
          sx={{ textTransform: "none" }}
          className="_wg-dynamic-text"
        >
          Select File
        </Button>
        <Typography
          variant="body1"
          className="_wg-dynamic-text file-format-text"
          sx={{ mb: 1 }}
          style={{ color: "rgb(211, 47, 47)" }}
        >
          <b>
            {typeUpload === "bulk-upload"
              ? `Supported format: ${allowedFormats} only (Max. file size: 50MB or Max Record Limit: 1000 per file)`
              : typeUpload === "translitration"
                ? `supported format: ${allowedFormats} only (Max. file size: 50MB or Max Record Limit: 2000 per file)`
                : searchType === "basic" && searchIn === "eph"
                  ? `Supported format: ${allowedFormats} only (Max. file size: 50MB or Max Record Limit: 50000 per file)`
                  : searchType === "basic" && searchIn === "vendor"
                    ? `Supported format: ${allowedFormats} only (Max. file size: 50MB or Max Record Limit: 10000 per file)`
                    : `Supported format: ${allowedFormats} only (Max. file size: 50MB or Max Record Limit: 1000 per file)`}
          </b>
        </Typography>
      </div>
      <input
        ref={inputRef}
        type="file"
        onChange={handleFileUpload}
        accept={allowedFormats}
        className="d-none"
      />
      {selectedFile && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            backgroundColor: "white",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            gap: 2,
            marginTop: 2,
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <DescriptionOutlinedIcon fontSize="medium" className="file-icon" />
            <Typography variant="text">
              {selectedFile.name}{" "}
              <span className="file-size">
                {selectedFile.size >= 1024 * 1024
                  ? `${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`
                  : `${(selectedFile.size / 1024).toFixed(2)} KB`}
              </span>
            </Typography>
          </div>
          <IconButton
            onClick={handleRemoveFile}
            size="small"
            sx={{
              color: "#d32f2f",
              "&:hover": {
                backgroundColor: "rgba(211, 47, 47, 0.04)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}
      {error && (
        <div className="selected-file-container invalid-file mt-2">
          <p className="selected-file-name _wg-dynamic-text">
            <strong>Error:</strong> {error}
          </p>
        </div>
      )}
    </div>
  );
};

export default Dropzone;